/**
 * components/site-footer — ПЕРЕИСПОЛЬЗУЕМЫЙ подвал юр/инфо-ссылок (Privacy Policy /
 * About / …). Тонкий примитив-«ствол»: монтируется на любой surface, сам не знает,
 * на какой странице стоит (сейчас — низ скролл-лендинга start-screen).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-26 «модульность/примитивы — чтобы
 * легко масштабировать при добавлении языков»): ярлык и путь КАЖДОЙ ссылки берутся из
 * ЕДИНОГО ИСТОЧНИКА — контент-бандла (`resolveContentPage(lang, slug).page.navLabel`
 * + `contentPath`), где страница САМА себя именует (frontmatter своей `.md`). Поэтому
 * добавление языка = добавление `content/<lang>/<slug>.md` (как и требует i18n-каталог)
 * → подвал локализуется и подхватывает ссылку АВТОМАТИЧЕСКИ, без правок этого модуля.
 * Никаких 15 захардкоженных переводов в коде (анти-паттерн, отверг owner).
 *
 * SCOPE: рендер ряда контент-ссылок + self-contained SPA-навигация (navigateContent).
 * NOT IN SCOPE: layout хоста, выбор какие slug'и показывать (передаёт caller; дефолт —
 *   privacy), генерация бандла (scripts/build-content).
 *
 * Cross-references: lib/content-routing (resolveContentPage / navigateContent — единый
 *   ствол контент-роутинга); @lines98/shared/config/content-routes (contentPath /
 *   normalizeSlug); responsive-ui §5.0 (layout-plan — в site-footer.css).
 */

import './site-footer.css';

import type { ContentLang } from '@lines98/shared/config/seo';
import { contentPath, normalizeSlug } from '@lines98/shared/config/content-routes';

import { navigateContent, resolveContentPage } from '../lib/content-routing.js';

export interface SiteFooterOptions {
  /** Язык подвала (= язык поверхности-хоста). */
  readonly lang: ContentLang;
  /**
   * Слаги юр/инфо-страниц в порядке отображения. Дефолт — только Privacy Policy.
   * Несуществующая на этом языке страница ТИХО пропускается (резолв-фолбэк отсечён) —
   * битых ссылок в подвале не бывает.
   */
  readonly slugs?: readonly string[];
  /**
   * Доп. action-ссылки (НЕ контент-страницы, а действия — напр. «♥ Поддержать» →
   * игровой маршрут). Рендерятся после контент-ссылок тем же стилем; клик → onClick
   * (self-contained, не navigateContent). Локализованную подпись даёт caller.
   */
  readonly actions?: readonly { readonly label: string; readonly onClick: () => void }[];
}

export interface SiteFooterHandle {
  readonly element: HTMLElement;
  destroy(): void;
}

/** Дефолт: подвал несёт ссылку на Политику конфиденциальности. */
const DEFAULT_FOOTER_SLUGS = ['privacy'] as const;

/** HTML-escape для текстовых позиций (ярлыки из бандла — доверенные, но гигиена). */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface FooterLink {
  readonly slug: string;
  readonly label: string;
  readonly href: string;
}

/**
 * Резолв ссылок из бандла (единый источник). Только реально существующие страницы:
 * если `resolveContentPage` сфолбэчил на лендинг (slug не найден), пункт отбрасываем.
 */
function resolveLinks(lang: ContentLang, slugs: readonly string[]): FooterLink[] {
  const out: FooterLink[] = [];
  for (const slug of slugs) {
    const want = normalizeSlug(slug);
    const { page } = resolveContentPage(lang, want);
    if (page.slug !== want) continue; // фолбэк → страницы нет на этом языке: пропускаем
    out.push({ slug: page.slug, label: page.navLabel, href: contentPath(lang, page.slug) });
  }
  return out;
}

/**
 * Смонтировать подвал в `host`. Возвращает хэндл с `destroy()`. Клик по ссылке —
 * self-contained SPA-переход через ствол `navigateContent` (модуль не зависит от
 * делегирования хоста → переиспользуем на любой поверхности). Модификатор-клик /
 * средняя кнопка — отдаём браузеру (нативное открытие href, в т.ч. в новой вкладке).
 */
export function mountSiteFooter(host: HTMLElement, opts: SiteFooterOptions): SiteFooterHandle {
  const slugs = opts.slugs ?? DEFAULT_FOOTER_SLUGS;
  const links = resolveLinks(opts.lang, slugs);

  const footer = document.createElement('footer');
  footer.className = 'site-footer';

  const actions = opts.actions ?? [];
  const linkParts = links.map(
    (l) =>
      `<a class="site-footer-link" data-slug="${esc(l.slug)}" href="${esc(l.href)}">${esc(
        l.label,
      )}</a>`,
  );
  // Action-ссылки — `<button>` тем же видом (.site-footer-link), но без href: это
  // действие (игровой маршрут), а не контент-страница. Клик → opts.actions[i].onClick.
  const actionParts = actions.map(
    (a, i) =>
      `<button type="button" class="site-footer-link site-footer-action" data-action="${i}">${esc(
        a.label,
      )}</button>`,
  );
  footer.innerHTML = [...linkParts, ...actionParts].join(
    '<span class="site-footer-sep" aria-hidden="true">·</span>',
  );

  const onClick = (e: MouseEvent): void => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return; // модификатор/средняя кнопка → нативное открытие href
    }
    const target = e.target as HTMLElement | null;
    // Action-ссылка (напр. «♥ Поддержать») → self-contained onClick (игровой маршрут).
    const act = target?.closest<HTMLElement>('.site-footer-action');
    if (act) {
      e.preventDefault();
      actions[Number(act.getAttribute('data-action'))]?.onClick();
      return;
    }
    const a = target?.closest<HTMLElement>('a.site-footer-link');
    if (!a) return;
    e.preventDefault();
    navigateContent(opts.lang, a.getAttribute('data-slug') ?? '');
  };
  footer.addEventListener('click', onClick);

  host.appendChild(footer);

  return {
    element: footer,
    destroy(): void {
      footer.removeEventListener('click', onClick);
      footer.remove();
    },
  };
}
